<template>
  <div>
    <h1 class="text-3xl font-bold mb-6 text-center text-gray-800">Review Grading Instructions</h1>
    <p class="text-center text-gray-600 mb-4">
      Our AI has generated draft instructions. Please review and edit them below before processing the test.
    </p>

    <!-- Loading Spinner for the initial data fetch -->
    <div v-if="loading" class="text-center py-10">
      <svg class="animate-spin h-8 w-8 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm0 0a8 8 0 018 8v-2a6 6 0 00-6-6H4z"></path>
      </svg>
      <p class="mt-2 text-gray-600">Loading generated instructions...</p>
    </div>

    <!-- Error Message -->
    <div v-else-if="error" class="text-center text-red-500 bg-red-50 p-4 rounded">
      <p>Failed to load the instructions.</p>
      <p class="text-sm text-red-700 mt-1">{{ error.message }}</p>
    </div>

    <!-- Instructions Content -->
    <textarea
        v-else
        v-model="instructionContent"
        class="w-full border border-gray-300 rounded-lg p-4 bg-white"
        rows="20"
        ref="textarea"
        @input="autoResize"
    ></textarea>

    <!-- Navigation Buttons are now added to this component -->
    <div class="flex justify-between mt-8">
      <button
          @click="goBack"
          class="bg-gray-500 text-white px-4 py-2 rounded transition hover:bg-gray-600 flex items-center min-w-[120px] justify-center"
          :disabled="navLoading"
      >
        Back
      </button>

      <button
          @click="triggerNext"
          class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center min-w-[120px] justify-center"
          :disabled="navLoading">
        <!-- The button spinner is gone because the modal replaces it -->
        <span>{{ buttonText }}</span>
      </button>
    </div>

    <!-- ****** THIS IS THE FIX ****** -->
    <!-- Add the processing modal here. It's hidden by default. -->
    <ProcessingModal
        :show="showProcessingModal"
        message="Processing your test... This may take up to 2 minutes."
    />
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '../services/axiosInstance'
import { useModalStore } from '@/stores/modal'
// Import the new modal component
import ProcessingModal from '@/components/ProcessingModal.vue'

const router = useRouter()
const route = useRoute()
const testId = route.params.testId
const modal = useModalStore()

const instructionContent = ref('')
const loading = ref(true) // For initial data fetch
const navLoading = ref(false) // A general disabled state for buttons
const error = ref(null)
const textarea = ref(null)

// A new ref to control the modal's visibility
const showProcessingModal = ref(false)

const buttonText = computed(() => 'Process Test')

const autoResize = () => {
  nextTick(() => {
    if (textarea.value) {
      textarea.value.style.height = 'auto'
      textarea.value.style.height = `${textarea.value.scrollHeight}px`
    }
  })
}

const fetchInstructions = async () => {
  if (!testId) {
    error.value = new Error('Cannot fetch instructions without a Test ID.')
    loading.value = false
    return
  }
  try {
    const response = await axios.get(`/tests/${testId}/instruction`)
    instructionContent.value = response.data.instruction
    autoResize()
  } catch (err) {
    console.error('Error fetching instructions:', err)
    error.value = err
    instructionContent.value = 'Could not load the generated instructions. Please enter them manually.'
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.go(-1)
}

const triggerNext = async () => {
  if (!testId) {
    await modal.error('Cannot proceed, Test ID is missing.')
    return
  }

  // Show the modal and disable buttons
  showProcessingModal.value = true
  navLoading.value = true
  try {
    await axios.put(`/tests/${testId}/instruction`, {
      instruction: instructionContent.value
    })

    await axios.post(`/tests/${testId}/process-all`, {}, {
      timeout: 240000 // 4 minutes
    })

    router.push({ name: 'AnswersOverview', params: { testId } })
  } catch (err) {
    if (err.code === 'ECONNABORTED') {
      await modal.error('The main test processing timed out after 4 minutes. Please check your materials and try again.')
    } else {
      await modal.error('A critical error occurred during test processing.')
    }
    console.error('Error in triggerNext for instructions:', err)
  } finally {
    // Hide the modal and re-enable buttons regardless of success or failure
    showProcessingModal.value = false
    navLoading.value = false
  }
}

onMounted(fetchInstructions)
</script>
